import axios from 'axios';
import { authHeaders } from '../auth/apiClient';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:7071';

/**
 * Upload a raw PDF and get its page count.
 * The file stays in the browser; we only send it here to learn its page count.
 *
 * @param {File} file
 * @returns {Promise<{page_count: number, file_size_kb: number, filename: string}>}
 */
export async function previewPdf(file) {
    const form = new FormData();
    form.append('file', file);

    const { data } = await axios.post(`${API_BASE}/api/pdf-preview`, form, {
        headers: await authHeaders({ 'Content-Type': 'multipart/form-data' }),
        timeout: 60_000,
    });

    if (!data.success) throw new Error(data.error || 'PDF preview failed');
    return data.data;
}

/**
 * Upload the raw PDF with a page selection and trigger AI ingestion.
 *
 * @param {File}     file           — The raw PDF File object
 * @param {number[]} selectedPages  — 1-based sorted array of page numbers
 * @param {object}   metadata
 * @param {string}   metadata.companyId
 * @param {string}   metadata.companyName
 * @param {number}   metadata.fiscalYear
 * @param {string}   [metadata.documentType='annual_report']
 * @param {string}   [metadata.basis='consolidated']
 * @returns {Promise<{job_id: string, status: string, selected_page_count: number, original_page_count: number}>}
 */
export async function ingestSelectedPages(file, selectedPages, metadata) {
    const form = new FormData();
    form.append('file', file);
    form.append('selectedPages', JSON.stringify(selectedPages));
    form.append('companyId', metadata.companyId);
    form.append('companyName', metadata.companyName);
    form.append('fiscalYear', String(metadata.fiscalYear));
    form.append('documentType', metadata.documentType || 'annual_report');
    form.append('basis', metadata.basis || 'consolidated');

    const { data } = await axios.post(`${API_BASE}/api/pdf-ingest-selected`, form, {
        headers: await authHeaders({ 'Content-Type': 'multipart/form-data' }),
        timeout: 300_000, // 5 min — large PDFs take time to upload + clean
    });

    if (!data.success) throw new Error(data.error || 'Ingest request failed');
    return data.data;
}

/**
 * Poll the job status for a given companyId.
 * Returns the most recent job record (sorted by created_at desc).
 *
 * @param {string} companyId
 * @param {string} jobId
 * @returns {Promise<object|null>}
 */
export async function getJobStatus(companyId, jobId) {
    const { data } = await axios.get(`${API_BASE}/api/documents`, {
        params: { companyId, jobId },   // direct lookup — backend filters to single job
        headers: await authHeaders(),
        timeout: 30_000,
    });

    if (!data.success) return null;
    const jobs = data.data || [];
    // Backend returns just the matching job when jobId is passed, but handle both cases
    return jobs.find((j) => j.job_id === jobId || j._id === jobId) || jobs[0] || null;
}
